'use client';

import { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowRight, Phone, Mail, MapPin, Home, Star } from 'lucide-react';
import { Label } from '@/components/ui/label';

export default function PropertyOwnerCTA() {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        propertyType: '',
        location: '',
        bedrooms: '',
        message: ''
    });

    const handleInputChange = (field: string, value: string) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        // Handle form submission here
        console.log('Form submitted:', formData);
        // You can add your form submission logic here
    };

    return (
        <section className="py-20 bg-gradient-to-br from-primary/10 to-primary/5">
            <div className="container mx-auto px-4">
                <div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                        {/* Left Side - Content */}
                        <div className="text-black">
                            <h2 className="text-3xl lg:text-4xl font-semibold mb-6">
                                Ready to Start Earning?
                            </h2>
                            <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                                Join our platform today and start earning from your property. 
                                Our team will guide you through the entire process.
                            </p>

                            <div className="space-y-6 mb-8">
                                <div className="flex items-center gap-4 group">
                                    <div className="w-12 h-12 bg-secondary text-white group-hover:bg-white group-hover:text-secondary group-hover:border group-hover:border-secondary rounded-full transition-all duration-300 shadow-lg flex items-center justify-center">
                                        <Star className="w-6 h-6" />
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-semibold">Free Property Assessment</h3>
                                        <p className="text-gray-600">Get a personalized revenue estimate for your property</p>
                                    </div>
                                </div>

                                <div className="flex items-center gap-4 group">
                                    <div className="w-12 h-12 bg-secondary text-white group-hover:bg-white group-hover:text-secondary group-hover:border group-hover:border-secondary rounded-full transition-all duration-300 shadow-lg flex items-center justify-center">
                                        <Home className="w-6 h-6" />
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-semibold">No Setup Fees</h3>
                                        <p className="text-gray-600">Start listing immediately with zero upfront costs</p>
                                    </div>
                                </div>

                                <div className="flex items-center gap-4 group">
                                    <div className="w-12 h-12 bg-secondary text-white group-hover:bg-white group-hover:text-secondary group-hover:border group-hover:border-secondary rounded-full transition-all duration-300 shadow-lg flex items-center justify-center">
                                        <Phone className="w-6 h-6" />
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-semibold">Dedicated Support</h3>
                                        <p className="text-gray-600">Personal property manager assigned to your listing</p>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white/10 rounded-lg p-6">
                                <h3 className="text-lg font-semibold mb-4">Get Started Today</h3>
                                <div className="space-y-3 text-sm">
                                    <div className="flex items-center gap-2">
                                        <div className="w-2 h-2 bg-secondary rounded-full"></div>
                                        <span>Property assessment within 24 hours</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="w-2 h-2 bg-secondary rounded-full"></div>
                                        <span>Professional photography scheduled</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="w-2 h-2 bg-secondary rounded-full"></div>
                                        <span>Listing goes live within 48 hours</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Right Side - Form */}
                        <div className="bg-white rounded-2xl p-8 shadow-2xl">
                            <h3 className="text-2xl font-bold text-gray-900 mb-6">
                                List Your Property
                            </h3>
                            
                            <form onSubmit={handleSubmit} className="space-y-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <Label className="block text-sm font-medium text-gray-700 mb-2">
                                            Full Name *
                                        </Label>
                                        <Input
                                            type="text"
                                            value={formData.name}
                                            onChange={(e) => handleInputChange('name', e.target.value)}
                                            placeholder="Your full name"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <Label className="block text-sm font-medium text-gray-700 mb-2">
                                            Email Address *
                                        </Label>
                                        <Input
                                            type="email"
                                            value={formData.email}
                                            onChange={(e) => handleInputChange('email', e.target.value)}
                                            placeholder="your@email.com"
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <Label className="block text-sm font-medium text-gray-700 mb-2">
                                            Phone Number *
                                        </Label>
                                        <Input
                                            type="tel"
                                            value={formData.phone}
                                            onChange={(e) => handleInputChange('phone', e.target.value)}
                                            placeholder="+971 XX XXX XXXX"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <Label className="block text-sm font-medium text-gray-700 mb-2">
                                            Property Type *
                                        </Label>
                                        <Select value={formData.propertyType} onValueChange={(value) => handleInputChange('propertyType', value)}>
                                            <SelectTrigger>
                                                <SelectValue placeholder="Select property type" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="apartment">Apartment</SelectItem>
                                                <SelectItem value="villa">Villa</SelectItem>
                                                <SelectItem value="townhouse">Townhouse</SelectItem>
                                                <SelectItem value="studio">Studio</SelectItem>
                                                <SelectItem value="penthouse">Penthouse</SelectItem>
                                                <SelectItem value="other">Other</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <Label className="block text-sm font-medium text-gray-700 mb-2">
                                            Location *
                                        </Label>
                                        <Input
                                            type="text"
                                            value={formData.location}
                                            onChange={(e) => handleInputChange('location', e.target.value)}
                                            placeholder="e.g., Palm Jumeirah, Dubai"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <Label className="block text-sm font-medium text-gray-700 mb-2">
                                            Number of Bedrooms *
                                        </Label>
                                        <Select value={formData.bedrooms} onValueChange={(value) => handleInputChange('bedrooms', value)}>
                                            <SelectTrigger>
                                                <SelectValue placeholder="Select bedrooms" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="studio">Studio</SelectItem>
                                                <SelectItem value="1">1 Bedroom</SelectItem>
                                                <SelectItem value="2">2 Bedrooms</SelectItem>
                                                <SelectItem value="3">3 Bedrooms</SelectItem>
                                                <SelectItem value="4">4 Bedrooms</SelectItem>
                                                <SelectItem value="5+">5+ Bedrooms</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>

                                <div>
                                    <Label className="block text-sm font-medium text-gray-700 mb-2">
                                        Tell us about your property
                                    </Label>
                                    <Textarea
                                        value={formData.message}
                                        onChange={(e) => handleInputChange('message', e.target.value)}
                                        placeholder="Describe your property, amenities, and any special features..."
                                        rows={4}
                                    />
                                </div>

                                <Button 
                                    type="submit" 
                                    size="lg" 
                                    // className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                                >
                                    Get Started Today
                                    <ArrowRight className="ml-2 w-5 h-5" />
                                </Button>

                                <p className="text-sm text-gray-500 text-center">
                                    By submitting this form, you agree to our terms of service and privacy policy.
                                </p>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
